// Unified Supabase Client Configuration
import { createClient } from '@supabase/supabase-js'

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing required Supabase environment variables')
}

// Global error handler to suppress "Already Used" refresh token errors
// These are handled gracefully in our auth flow, so we don't want to log them
if (typeof window !== 'undefined') {
  const originalError = console.error
  console.error = (...args) => {
    // Suppress "Already Used" and "Invalid Refresh Token" errors from Supabase
    const message = args.join(' ')
    if (message.includes('Already Used') ||
        message.includes('Invalid Refresh Token') ||
        message.includes('AuthApiError: Invalid Refresh Token')) {
      // Log a cleaner message instead
      console.warn('🔄 Token refresh detected - will re-authenticate if needed')
      return
    }
    // Pass through all other errors
    originalError.apply(console, args)
  }
}

// Create unified Supabase client with cross-domain session support
export const supabase = createClient(
  supabaseUrl,
  supabaseAnonKey,
  {
    auth: {
      // Use localStorage with consistent storage key
      storage: typeof window !== 'undefined' ? window.localStorage : null,
      storageKey: 'supabase.auth.token', // Consistent with website
      // Enable automatic refresh of tokens
      autoRefreshToken: true,
      // Persist session across page refreshes
      persistSession: true,
      // Detect session in URL for redirects from website
      detectSessionInUrl: true,
      // Environment-aware cookie configuration
      cookieOptions: (() => {
        const isDevelopment = process.env.NODE_ENV === 'development'
        const isLocalhost = window.location.hostname === 'localhost'

        if (isDevelopment || isLocalhost) {
          // Development configuration - no domain restriction
          return {
            path: '/',
            secure: false, // Allow HTTP in development
            sameSite: 'lax'
          }
        } else {
          // Production configuration - cross-subdomain support
          return {
            domain: '.atsinsiderai.com',
            path: '/',
            secure: true, // HTTPS only in production
            sameSite: 'lax'
          }
        }
      })()
    },
    // Additional headers for consistency
    global: {
      headers: {
        'X-Client-Info': 'atsinsiderai-app/1.0.0',
      },
    },
  }
)

// Enhanced session helper with security checks
// IMPORTANT: Let Supabase handle token refreshing automatically via autoRefreshToken
// Manual refresh causes race conditions and "Already Used" errors
export const getSecureSession = async () => {
  try {
    const { data: { session }, error } = await supabase.auth.getSession()

    if (error) {
      // Special handling for refresh token errors
      if (error.message?.includes('Already Used') || error.message?.includes('Invalid Refresh Token')) {
        console.warn('🔄 Refresh token issue detected - clearing stale tokens')
        // Clear stale tokens and force re-auth
        if (typeof window !== 'undefined') {
          Object.keys(localStorage).forEach(key => {
            if (key.startsWith('sb-') || key.includes('supabase')) {
              localStorage.removeItem(key)
            }
          })
        }
        await supabase.auth.signOut()
      }
      console.error('Session retrieval error:', error.message)
      return null
    }

    // Return session without manual refresh attempts
    // Supabase's autoRefreshToken handles this automatically in the background
    return session
  } catch (error) {
    console.error('Error in getSecureSession:', error)
    return null
  }
}

// Secure API call helper with enterprise-grade security
export const secureApiCall = async (endpoint, options = {}) => {
  const apiUrl = process.env.REACT_APP_API_URL

  // SANDBOX MODE: Skip auth checks
  const isSandbox = apiUrl && apiUrl.includes('sandbox')

  if (isSandbox) {
    // In sandbox, just make the API call without auth headers
    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), options.timeout || 30000)

    try {
      const response = await fetch(`${apiUrl}${endpoint}`, {
        ...options,
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          ...options.headers
        }
      })

      clearTimeout(timeout)

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Network error' }))
        throw new Error(error.message || `HTTP ${response.status}`)
      }

      return response.json()
    } catch (error) {
      clearTimeout(timeout)
      if (error.name === 'AbortError') {
        throw new Error('Request timeout')
      }
      throw error
    }
  }

  // PRODUCTION MODE: Use full auth
  const session = await getSecureSession()

  if (!session) {
    throw new Error('Authentication required. Please sign in.')
  }

  // Get CSRF token for state-changing operations
  let csrfToken = null
  if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(options.method?.toUpperCase())) {
    try {
      const csrfController = new AbortController()
      const csrfTimeout = setTimeout(() => csrfController.abort(), 5000) // 5 second timeout

      const csrfResponse = await fetch(`${apiUrl}/csrf`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        signal: csrfController.signal
      })

      clearTimeout(csrfTimeout)

      if (csrfResponse.ok) {
        const csrfData = await csrfResponse.json()
        csrfToken = csrfData.csrf_token
      }
    } catch (error) {
      // Don't block API calls if CSRF fails - log and continue
      console.warn('Could not retrieve CSRF token:', error.name === 'AbortError' ? 'Timeout' : error.message)
    }
  }

  // Main API call with timeout
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), options.timeout || 30000) // 30 second default

  try {
    const response = await fetch(`${apiUrl}${endpoint}`, {
      ...options,
      credentials: 'include',
      signal: controller.signal,
      headers: {
        'Authorization': `Bearer ${session.access_token}`,
        'Content-Type': 'application/json',
        'X-Client-Info': 'atsinsiderai-app/1.0.0',
        ...(csrfToken && { 'X-CSRF-Token': csrfToken }),
        ...options.headers
      }
    })

    clearTimeout(timeout)

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: 'Network error' }))
      throw new Error(error.message || `HTTP ${response.status}`)
    }

    return response.json()
  } catch (error) {
    clearTimeout(timeout)

    if (error.name === 'AbortError') {
      throw new Error('Request timeout - please check your connection and try again')
    }

    throw error
  }
}

// Simple backward compatibility for getSession (used by StatusPage/AuthWrapper)
export const getSession = async () => {
  const { data: { session }, error } = await supabase.auth.getSession()
  if (error) {
    console.error('Error getting session:', error)
    return null
  }
  return session
}